import {
    type InfiniteData,
    useInfiniteQuery,
    useQuery,
    useQueryClient,
} from '@tanstack/react-query';
import type {
    Notification as MagicBellSDKNotification,
    Client as MagicBellUserClient,
} from 'magicbell-js/user-client';
import * as React from 'react';

import { useEventCallback, useLiveRef } from '@gitbook/util-react-hooks';

import { useCurrentUser } from '../../state';
import {
    useMagicBellAvailability,
    useMagicBellClient,
    useMagicBellSubscription,
} from './magicbell';
import { getMagicBellNotificationsQueryKey, getMagicBellUnreadCountQueryKey } from './queryKeys';

const MAGICBELL_PAGE_SIZE = 50;

type NotificationsPage = {
    notifications: MagicBellSDKNotification[];
    nextCursor?: string;
};

type MagicBellClient = NonNullable<MagicBellUserClient>;

/**
 * Public notification type used by the notifications UI and analytics helpers.
 */
export type NotificationData = Omit<
    MagicBellSDKNotification,
    'actionUrl' | 'category' | 'content' | 'customAttributes' | 'readAt' | 'sentAt' | 'topic'
> & {
    actionUrl: string | null;
    category: string;
    content: string;
    customAttributes: unknown;
    readAt: Date | null;
    sentAt: Date | null;
    topic: string;
    isRead: boolean;
    markAsRead: () => Promise<boolean>;
    markAsUnread: () => Promise<boolean>;
    delete: () => Promise<boolean>;
};

/**
 * Returns the full notification store used by the notifications panel.
 */
export function useNotifications(): {
    notifications: NotificationData[];
    fetchNextPage: () => Promise<void>;
    hasNextPage: boolean;
    isEmpty: boolean;
    unreadCount?: number;
    lastFetchedAt?: Date;
    markAllAsRead: () => Promise<boolean>;
} | null {
    const { status } = useMagicBellAvailability();
    const client = useMagicBellClient();
    const queryClient = useQueryClient();
    const userId = useCurrentUser()?.uid ?? null;
    const unreadCountQuery = useUnreadNotificationsCountQuery(status, client, userId);

    const notificationsQuery = useInfiniteQuery({
        queryKey: getMagicBellNotificationsQueryKey(userId),
        enabled: status === 'ready' && Boolean(client),
        initialPageParam: undefined as string | undefined,
        queryFn: async ({ pageParam }) => {
            if (!client) {
                throw new Error('MagicBell client is not available');
            }

            const { data, metadata } = await client.notifications.listNotifications({
                limit: MAGICBELL_PAGE_SIZE,
                startingAfter: pageParam,
            });

            assertResponseOk(metadata.status, metadata.statusText);

            return {
                notifications: data?.data ?? [],
                nextCursor: extractCursor(data?.links?.next),
            };
        },
        getNextPageParam: (page) => page.nextCursor,
    });

    const markAllAsRead = useEventCallback(async () => {
        if (!client) {
            return false;
        }

        markAllNotificationsReadInCache(queryClient, userId);

        try {
            const { metadata } = await client.notifications.markAllNotificationsRead();
            assertResponseOk(metadata.status, metadata.statusText);
            return true;
        } catch (error) {
            await invalidateNotificationQueries(queryClient, userId);
            throw error;
        }
    });

    if (status !== 'ready' || !client) {
        return null;
    }

    const allNotifications = (notificationsQuery.data?.pages ?? []).flatMap((page) =>
        page.notifications.map((notification) =>
            createNotification(notification, client, queryClient, userId)
        )
    );

    return {
        notifications: allNotifications,
        fetchNextPage: async () => {
            if (notificationsQuery.hasNextPage) {
                await notificationsQuery.fetchNextPage();
            }
        },
        hasNextPage: Boolean(notificationsQuery.hasNextPage),
        isEmpty: allNotifications.length === 0,
        unreadCount: unreadCountQuery.data,
        lastFetchedAt: notificationsQuery.dataUpdatedAt
            ? new Date(notificationsQuery.dataUpdatedAt)
            : undefined,
        markAllAsRead,
    };
}

/**
 * Returns only the unread notification count for the current user.
 */
export function useUnreadNotificationsCount(): number | undefined {
    const { status } = useMagicBellAvailability();
    const client = useMagicBellClient();
    const userId = useCurrentUser()?.uid ?? null;
    const unreadCountQuery = useUnreadNotificationsCountQuery(status, client, userId);

    if (status !== 'ready' || !client) {
        return undefined;
    }

    return unreadCountQuery.data;
}

/**
 * Subscribes to MagicBell notification events emitted by the realtime client.
 */
export function useMagicBellEvent(
    eventName: 'notifications.new',
    callback: (notification: NotificationData) => void
): void {
    const client = useMagicBellClient();
    const queryClient = useQueryClient();
    const subscribe = useMagicBellSubscription();
    const userId = useCurrentUser()?.uid ?? null;
    const callbackRef = useLiveRef(callback);

    React.useEffect(() => {
        if (eventName !== 'notifications.new' || !client) {
            return;
        }

        return subscribe((notification) => {
            callbackRef.current(createNotification(notification, client, queryClient, userId));
        });
    }, [client, eventName, queryClient, subscribe, userId, callbackRef]);
}

function useUnreadNotificationsCountQuery(
    status: 'loading' | 'ready' | 'disabled',
    client: MagicBellUserClient | null,
    userId: string | null
) {
    return useQuery({
        queryKey: getMagicBellUnreadCountQueryKey(userId),
        enabled: status === 'ready' && Boolean(client),
        queryFn: async () => {
            if (!client) {
                throw new Error('MagicBell client is not available');
            }

            const { data, metadata } = await client.notifications.fetchUnreadNotificationsCount();
            assertResponseOk(metadata.status, metadata.statusText);
            return data?.count ?? 0;
        },
    });
}

function createNotification(
    notification: MagicBellSDKNotification,
    client: MagicBellClient,
    queryClient: ReturnType<typeof useQueryClient>,
    userId: string | null
): NotificationData {
    return {
        ...notification,
        actionUrl: notification.actionUrl ?? null,
        category: notification.category ?? '',
        content: notification.content ?? '',
        customAttributes: notification.customAttributes,
        readAt: parseDate(notification.readAt),
        sentAt: parseDate(notification.sentAt),
        isRead: Boolean(notification.readAt),
        topic: notification.topic ?? '',
        markAsRead: async () => markNotificationRead(client, queryClient, notification.id, userId),
        markAsUnread: async () =>
            markNotificationUnread(client, queryClient, notification.id, userId),
        delete: async () => archiveNotification(client, queryClient, notification.id, userId),
    };
}

async function markNotificationRead(
    client: MagicBellClient,
    queryClient: ReturnType<typeof useQueryClient>,
    notificationId: string,
    userId: string | null
): Promise<boolean> {
    const cachedNotification = getCachedNotification(queryClient, notificationId, userId);
    const shouldDecrementUnreadCount = Boolean(cachedNotification && !cachedNotification.readAt);
    const shouldRefreshUnreadCount = !cachedNotification;

    return runNotificationMutation(
        queryClient,
        {
            applyOptimisticUpdate: () => {
                updateNotificationInCache(queryClient, notificationId, userId, (notification) => ({
                    ...notification,
                    readAt: notification.readAt ?? new Date().toISOString(),
                }));

                if (shouldDecrementUnreadCount) {
                    updateUnreadCountInCache(queryClient, userId, (count) =>
                        Math.max(0, count - 1)
                    );
                }
            },
            execute: async () => {
                const { metadata } =
                    await client.notifications.markNotificationRead(notificationId);
                assertResponseOk(metadata.status, metadata.statusText);

                if (shouldRefreshUnreadCount) {
                    await queryClient.invalidateQueries({
                        queryKey: getMagicBellUnreadCountQueryKey(userId),
                    });
                }
            },
        },
        userId
    );
}

async function markNotificationUnread(
    client: MagicBellClient,
    queryClient: ReturnType<typeof useQueryClient>,
    notificationId: string,
    userId: string | null
): Promise<boolean> {
    const wasRead = Boolean(getCachedNotification(queryClient, notificationId, userId)?.readAt);

    return runNotificationMutation(
        queryClient,
        {
            applyOptimisticUpdate: () => {
                updateNotificationInCache(queryClient, notificationId, userId, (notification) => ({
                    ...notification,
                    readAt: null,
                }));

                if (wasRead) {
                    updateUnreadCountInCache(queryClient, userId, (count) => count + 1);
                }
            },
            execute: async () => {
                const { metadata } =
                    await client.notifications.markNotificationUnread(notificationId);
                assertResponseOk(metadata.status, metadata.statusText);
            },
        },
        userId
    );
}

async function archiveNotification(
    client: MagicBellClient,
    queryClient: ReturnType<typeof useQueryClient>,
    notificationId: string,
    userId: string | null
): Promise<boolean> {
    return runNotificationMutation(
        queryClient,
        {
            applyOptimisticUpdate: () => {
                let wasUnread = false;

                updateNotificationsCache(queryClient, userId, (page) => ({
                    ...page,
                    notifications: page.notifications.filter((notification) => {
                        if (notification.id === notificationId && !notification.readAt) {
                            wasUnread = true;
                        }
                        return notification.id !== notificationId;
                    }),
                }));

                if (wasUnread) {
                    updateUnreadCountInCache(queryClient, userId, (count) =>
                        Math.max(0, count - 1)
                    );
                }
            },
            execute: async () => {
                const { metadata } = await client.notifications.archiveNotification(notificationId);
                assertResponseOk(metadata.status, metadata.statusText);
            },
        },
        userId
    );
}

async function invalidateNotificationQueries(
    queryClient: ReturnType<typeof useQueryClient>,
    userId: string | null
) {
    await Promise.all([
        queryClient.invalidateQueries({ queryKey: getMagicBellNotificationsQueryKey(userId) }),
        queryClient.invalidateQueries({ queryKey: getMagicBellUnreadCountQueryKey(userId) }),
    ]);
}

async function runNotificationMutation(
    queryClient: ReturnType<typeof useQueryClient>,
    options: {
        applyOptimisticUpdate: () => void;
        execute: () => Promise<void>;
    },
    userId: string | null
): Promise<boolean> {
    options.applyOptimisticUpdate();

    try {
        await options.execute();
        return true;
    } catch (error) {
        await invalidateNotificationQueries(queryClient, userId);
        throw error;
    }
}

function markAllNotificationsReadInCache(
    queryClient: ReturnType<typeof useQueryClient>,
    userId: string | null
) {
    updateNotificationsCache(queryClient, userId, (page) => ({
        ...page,
        notifications: page.notifications.map((notification) => ({
            ...notification,
            readAt: notification.readAt ?? new Date().toISOString(),
        })),
    }));
    queryClient.setQueryData<number>(getMagicBellUnreadCountQueryKey(userId), 0);
}

function updateNotificationInCache(
    queryClient: ReturnType<typeof useQueryClient>,
    notificationId: string,
    userId: string | null,
    updater: (notification: MagicBellSDKNotification) => MagicBellSDKNotification
) {
    updateNotificationsCache(queryClient, userId, (page) => ({
        ...page,
        notifications: page.notifications.map((notification) =>
            notification.id === notificationId ? updater(notification) : notification
        ),
    }));
}

function getCachedNotification(
    queryClient: ReturnType<typeof useQueryClient>,
    notificationId: string,
    userId: string | null
): MagicBellSDKNotification | undefined {
    const data = queryClient.getQueryData<InfiniteData<NotificationsPage, string | undefined>>(
        getMagicBellNotificationsQueryKey(userId)
    );

    return data?.pages
        .flatMap((page) => page.notifications)
        .find((notification) => notification.id === notificationId);
}

function updateNotificationsCache(
    queryClient: ReturnType<typeof useQueryClient>,
    userId: string | null,
    updater: (page: NotificationsPage) => NotificationsPage
) {
    queryClient.setQueryData<InfiniteData<NotificationsPage, string | undefined>>(
        getMagicBellNotificationsQueryKey(userId),
        (data) => {
            if (!data) {
                return data;
            }

            return {
                ...data,
                pages: data.pages.map((page) => updater(page)),
            };
        }
    );
}

function updateUnreadCountInCache(
    queryClient: ReturnType<typeof useQueryClient>,
    userId: string | null,
    updater: (count: number) => number
) {
    queryClient.setQueryData<number>(getMagicBellUnreadCountQueryKey(userId), (count = 0) =>
        updater(count)
    );
}

function parseDate(value?: string | null): Date | null {
    return value ? new Date(value) : null;
}

function extractCursor(url?: string | null): string | undefined {
    if (!url) {
        return undefined;
    }

    return new URL(url).searchParams.get('starting_after') ?? undefined;
}

function assertResponseOk(status: number, statusText: string) {
    if (status < 200 || status >= 300) {
        throw new Error(`MagicBell returned ${status} ${statusText}`);
    }
}
